import { LinkListItem } from "../components/link-list";
import { CategoryJsonPage } from "../core/interfaces/JsonPage";
import Tag from "../core/interfaces/Tag";
import {
  VanillaApiTag,
  VanillaCategory,
  VanillaDiscussion,
} from "../core/interfaces/VanillaApi";
import ProviderFactory from "../core/ProviderFactory";
import {
  CommunityProviderType,
  ProviderConfiguration,
  TipsProvider,
  VanillaProvider,
} from "../core/types/Provider";
import { isMode } from "../core/utilities";
import EventModel from "./EventModel";

export enum CategoryType {
  discussions = "discussions",
  ideas = "ideas",
  events = "events",
  allEvents = "allEvents",
}

export default abstract class CategoryModel {
  private static provider: VanillaProvider = ProviderFactory.create(
    CommunityProviderType.Default,
    isMode("development")
      ? ProviderConfiguration.local
      : ProviderConfiguration.vanilla
  );
  private static tipsProvider: TipsProvider = ProviderFactory.create(
    CommunityProviderType.Tips,
    isMode("development")
      ? ProviderConfiguration.local
      : ProviderConfiguration.vanilla
  );
  private static categoryFromJsonPageCache: CategoryJsonPage = null;
  private static categoryTypeCache: CategoryType = null;
  private static parentTagCache: Tag = null;
  private static categoryTagsCache: Array<Tag> = null;
  private static currentTipTagCache: Tag = null;

  public static async getCategoryFromJsonPageCached(): Promise<CategoryJsonPage> {
    if (!CategoryModel.categoryFromJsonPageCache) {
      CategoryModel.categoryFromJsonPageCache =
        await CategoryModel.loadCategoryJson();
    }

    return CategoryModel.categoryFromJsonPageCache;
  }

  public static async loadCategoryJson(catUrl = ""): Promise<CategoryJsonPage> {
    const pageMetadata = await CategoryModel.provider.getCategoryFromJsonPage(
      catUrl
    );
    return pageMetadata;
  }

  public static getCategory(categoryId: number): Promise<VanillaCategory> {
    return new Promise((resolve, reject) => {
      CategoryModel.provider
        .getCategory(categoryId)
        .then((category) => {
          resolve(category);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public static getCategoryTypeCached(): Promise<CategoryType> {
    return new Promise((resolve, reject) => {
      if (!CategoryModel.categoryTypeCache) {
        EventModel.isGlobalEventsCategoryPage()
          .then((isAllEventsPage) => {
            if (isAllEventsPage) {
              resolve(CategoryType.allEvents);
            } else {
              return EventModel.isEventsCategoryPage();
            }
          })
          .then((isEventsCategory) => {
            if (isEventsCategory) {
              resolve(CategoryType.events);
            } else {
              return CategoryModel.getCategoryFromJsonPageCached();
            }
          })
          // Category from JSON page.
          .then((pageMetadata) => {
            if (pageMetadata && pageMetadata.Category) {
              const allowedDiscussionTypes: Array<string> =
                pageMetadata.Category.AllowedDiscussionTypes;
              if (allowedDiscussionTypes) {
                if (
                  allowedDiscussionTypes.some((type: string) => type === "Idea")
                ) {
                  resolve(CategoryType.ideas);
                } else if (
                  allowedDiscussionTypes.some(
                    (type: string) =>
                      type === "Question" || type === "Discussion"
                  )
                ) {
                  resolve(CategoryType.discussions);
                }
              }
            }
            reject("Impossible to know category type.");
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        resolve(CategoryModel.categoryTypeCache);
      }
    });
  }

  /**
   * This function returns the main parent tag associated with the category
   *   (e.g. "Category <catId>").
   * @returns {Tag} Main parent tag.
   */
  public static getMainParentTagCached(): Promise<Tag> {
    return new Promise((resolve, reject) => {
      if (!CategoryModel.parentTagCache) {
        CategoryModel.getCategoryFromJsonPageCached()
          .then((pageMetadata) => {
            const filter = [
              "query=Category " + pageMetadata.Category.CategoryID,
            ];
            return CategoryModel.provider.getTags(filter);
          })
          .then((tags) => {
            const parentTag = tags.find(
              (tag: Tag) =>
                tag.name ===
                "Category " +
                  CategoryModel.categoryFromJsonPageCache.Category.CategoryID
            );
            if (parentTag) {
              const tag: Tag = {
                id: parentTag.id,
                name: parentTag.name,
                urlCode: parentTag.urlCode,
              };
              CategoryModel.parentTagCache = tag;
              resolve(tag);
            } else {
              CategoryModel.parentTagCache = null;
              resolve(null);
            }
          })
          .catch((error) => {
            CategoryModel.parentTagCache = null;
            reject(error);
          });
      } else {
        resolve(CategoryModel.parentTagCache);
      }
    });
  }

  /**
   * This function returns all parent tags associated with the category
   *   (e.g. "Category <catId>", "Category <catId>-2", ..., "Category <catId>-n").
   * @returns {Array<Tag>} List of parent tags.
   */
  public static getParentTags(categoryId: number = null): Promise<Array<Tag>> {
    return new Promise((resolve, reject) => {
      const filter = ["query=Category " + categoryId];
      CategoryModel.provider
        .getTags(filter)
        .then((tags) => {
          const regex = new RegExp("Category " + categoryId + "(-\\d+)*$", "g");
          const parentTags = tags.filter((tag: Tag) => {
            const test = tag.name.match(regex);
            return test;
          });
          if (parentTags.length > 0) {
            const tags: Array<Tag> = parentTags.map((parentTag) => {
              return {
                id: parentTag.id,
                name: parentTag.name,
                urlCode: parentTag.urlCode,
              };
            });
            resolve(tags);
          } else {
            resolve([]);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * This function returns all category tags based on all parent tags associated with the category.
   * @param {number} cateegoryId Specific category to get tags.
   * @returns {Array<Tag>} List of category tags.
   */
  public static getCategoryTags(
    categoryId: number = null
  ): Promise<Array<Tag>> {
    return new Promise((resolve, reject) => {
      let parentTagsPromise: Promise<Array<Array<Tag>>> = null;
      if (categoryId) {
        // Use selected category.
        parentTagsPromise = new Promise((resolve) => {
          const getChildParentTagsCalls: Array<Promise<Array<Tag>>> = [];
          getChildParentTagsCalls.push(CategoryModel.getParentTags(categoryId));
          resolve(Promise.all(getChildParentTagsCalls));
        });
      } else {
        // Get current category metadata.
        parentTagsPromise = new Promise((resolve) => {
          resolve(
            CategoryModel.getCategoryFromJsonPageCached().then(
              (pageMetadata) => {
                const getChildParentTagsCalls: Array<Promise<Array<Tag>>> = [];
                if (pageMetadata.Category.ChildIDs?.length > 0) {
                  pageMetadata.Category.ChildIDs.forEach((subcategoryId) => {
                    getChildParentTagsCalls.push(
                      CategoryModel.getParentTags(subcategoryId)
                    );
                  });
                } else {
                  getChildParentTagsCalls.push(
                    CategoryModel.getParentTags(
                      pageMetadata.Category.CategoryID
                    )
                  );
                }
                return Promise.all(getChildParentTagsCalls);
              }
            )
          );
        });
      }
      if (parentTagsPromise) {
        // Promise all getParentTags calls.
        parentTagsPromise
          .then((vanilaParentTags) => {
            const parentTags = vanilaParentTags.reduce(
              (accumulator, current) => {
                return accumulator.concat(current);
              }
            );
            const getTagsCalls: Array<Promise<Array<VanillaApiTag>>> = [];
            if (parentTags && parentTags.length > 0) {
              parentTags.forEach((parentTag) => {
                const filter = ["parentID=" + parentTag.id, "limit=100"];
                getTagsCalls.push(CategoryModel.provider.getTags(filter));
              });
              return Promise.all(getTagsCalls);
            } else {
              CategoryModel.categoryTagsCache = null;
              resolve(null);
            }
          })
          // Promise all getTags calls.
          .then((vanillaCategoryTags) => {
            let categoryTags = vanillaCategoryTags.reduce(
              (accumulator, current) => accumulator.concat(current)
            );
            categoryTags = categoryTags.sort((a, b) => {
              if (a.name < b.name) {
                return -1;
              }
              if (a.name > b.name) {
                return 1;
              }
              return 0;
            });
            CategoryModel.categoryTagsCache = categoryTags;
            resolve(categoryTags);
          })
          .catch((error) => {
            CategoryModel.categoryTagsCache = null;
            reject(error);
          });
      }
    });
  }

  public static async lookupForCurrentTipTag(): Promise<Tag | null> {
    if (CategoryModel.currentTipTagCache === null) {
      try {
        const parentTag = await CategoryModel.getMainParentTagCached();
        if (parentTag) {
          CategoryModel.currentTipTagCache =
            await CategoryModel.tipsProvider.lookupForTipTag(parentTag.id);
        }
      } catch (e) {
        console.error(e);
      }
    }

    return CategoryModel.currentTipTagCache;
  }

  public static async getTipsForCurrentCategory(
    limit = 5
  ): Promise<Array<LinkListItem>> {
    try {
      const tipTag = await CategoryModel.lookupForCurrentTipTag();
      if (tipTag) {
        const discussions = await CategoryModel.tipsProvider.getTips(
          tipTag,
          limit
        );
        return discussions.map((discussion: VanillaDiscussion) => {
          return {
            id: discussion.discussionID,
            text: discussion.name,
            href: discussion.canonicalUrl || discussion.url,
            date: new Date(discussion.dateInserted).getTime(),
          };
        });
      }
    } catch (e) {
      console.error(e);
    }

    return [];
  }
}
